import { ArrowUpRight } from 'lucide-react'
import { useRouter } from 'next/router'
import * as React from 'react'

import { sanitizeHtmlValue } from '@/lib/element-schema-sanitizers'
import { sectionSchemaRegistry } from '@/lib/section-schema-registry'
import { cn } from '@/lib/utils'
import { useBranding } from '@/hooks/use-branding'

import { PageHeaderHeading } from '@/components/common/page-header'
import { Shell } from '@/components/common/shell'
import NextImage from '@/components/NextImage'

import type { SectionElementSchema } from '@/types/element-schema.type'

interface FeatureItem {
  title: string
  mobileTitle?: string
  description: string[] | Array<{ point: string }> | string
  image: string
  imageAlt: string
  href: string
}

/** Feature item after normalization — description is always string[] */
interface NormalizedFeatureItem extends Omit<FeatureItem, 'description'> {
  description: string[]
}

/**
 * Normalize feature items from Puck editor.
 * Puck array fields with children produce objects like {point: 'text'}
 * instead of plain strings. This normalizer handles both formats.
 */
function normalizeFeatures(features: FeatureItem[]): NormalizedFeatureItem[] {
  return features.map(feature => {
    let description: string[]
    if (Array.isArray(feature.description)) {
      description = feature.description.map(item =>
        typeof item === 'string' ? item : (item as { point: string }).point || ''
      )
    } else if (typeof feature.description === 'string' && feature.description) {
      description = [feature.description]
    } else {
      description = []
    }
    return { ...feature, description }
  })
}
interface FeatureSectionProps {
  className?: string
  /** Section heading (supports HTML) */
  heading?: string
  /** Heading text color */
  headingColor?: string
  /** Description text color */
  descriptionColor?: string
  /** Background color */
  bgColor?: string
  /** Data source: 'built-in' or 'custom' */
  dataSource?: 'built-in' | 'custom'
  /** Override default features (custom mode) */
  features?: FeatureItem[]
  /** Enable scrollspy navigation */
  enableScrollspy?: boolean
}

// Default features data
function getDefaultFeatures(siteName: string): FeatureItem[] {
  return [
    {
      title: 'Học',
      mobileTitle: 'Học',
      description: [
        '<b>Hành trình toàn diện: </b> Xây dựng năng lực Data & AI với 4 trụ cột: Modeling, Domain Knowledge, Analytics và Engineering',
        '<b>Trải nghiệm học tập khác biệt: </b> Giáo trình kết hợp kinh nghiệm thực tế và chương trình MSc Artificial Intelligence & Data Engineering @UCL (QS World University Rankings 2026 - Top 10, có đối tác học thuật nổi tiếng là Google DeepMind)',
      ],
      image: '/images/home/feature/learn.png',
      imageAlt: 'Zoom Meeting interface showing video conference participants',
      href: '/learning-path',
    },
    {
      title: 'Kết nối',
      mobileTitle: 'Kết nối',
      description: ['Mạng xã hội cho dân Data & AI, nơi mọi người học hỏi, hỗ trợ và phát triển cùng nhau.'],
      image: '/images/home/feature/connect.png',
      imageAlt: `${siteName} community forum interface with channels and posts`,
      href: '/forum',
    },
    {
      title: 'Phát triển sự nghiệp',
      mobileTitle: 'Sự nghiệp',
      description: [
        'Nơi việc làm hấp dẫn không chỉ đến từ đối tác tuyển dụng, mà còn được chia sẻ bởi cộng đồng, cùng những thông tin ẩn danh thú vị về công ty.',
      ],
      image: '/images/home/feature/job.png',
      imageAlt: 'Job board interface showing career opportunities',
      href: '/jobs',
    },
  ]
}

const defaultFeatures = getDefaultFeatures('')

// Element schema for Puck editor
export const featureSectionSchema: SectionElementSchema = {
  componentType: 'FeatureSection',
  label: 'Feature Section',
  category: 'content',
  description: 'Feature section with scrollspy navigation and tabbed content',
  elements: [
    {
      key: 'heading',
      type: 'textarea',
      label: 'Section Heading',
      helpText: 'Main heading (supports HTML)',
      default: 'Tham gia cộng đồng <span class="text-primary">Data & AI</span> cùng Platform',
    },
    {
      key: 'headingColor',
      type: 'color',
      label: 'Heading Color',
      helpText: 'Leave empty for default theme color',
      default: '',
    },
    {
      key: 'descriptionColor',
      type: 'color',
      label: 'Description Color',
      helpText: 'Leave empty for default theme color',
      default: '',
    },
    {
      key: 'bgColor',
      type: 'color',
      label: 'Background Color',
      default: 'transparent',
    },
    {
      key: 'dataSource',
      type: 'select',
      label: 'Data Source',
      options: [
        { label: 'Built-in (Default)', value: 'built-in' },
        { label: 'Custom Items', value: 'custom' },
      ],
      default: 'built-in',
    },
    {
      key: 'features',
      type: 'array',
      label: 'Custom Features',
      helpText: 'Add, edit, or remove feature items (only when Data Source is "Custom")',
      default: defaultFeatures,
      children: [
        { key: 'title', type: 'text', label: 'Title', required: true, default: 'New Feature' },
        { key: 'mobileTitle', type: 'text', label: 'Mobile Title', helpText: 'Shorter title for mobile' },
        {
          key: 'description',
          type: 'array',
          label: 'Description Points',
          helpText: 'Array of HTML description points',
          children: [{ key: 'point', type: 'textarea', label: 'Point (HTML)', default: 'Description text here' }],
        },
        {
          key: 'image',
          type: 'image',
          label: 'Feature Image',
          required: true,
          default: '/images/home/feature/learn.png',
        },
        { key: 'imageAlt', type: 'text', label: 'Image Alt Text', default: 'Feature image' },
        { key: 'href', type: 'text', label: 'Link URL', required: true, default: '#' },
      ],
    },
    {
      key: 'enableScrollspy',
      type: 'boolean',
      label: 'Enable Scroll Navigation',
      helpText: 'Show scrollspy-based tab navigation',
      default: true,
    },
    {
      key: 'className',
      type: 'text',
      label: 'CSS Class',
      helpText: 'Additional CSS classes',
    },
  ],
}

sectionSchemaRegistry.register(featureSectionSchema)

/** Placeholder SVG for missing images */
const ImagePlaceholder = () => (
  <div className="w-full aspect-[16/9] bg-gray-100 rounded-2xl flex items-center justify-center">
    <div className="text-center text-gray-400">
      <svg className="w-12 h-12 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0022.5 18.75V5.25A2.25 2.25 0 0020.25 3H3.75A2.25 2.25 0 001.5 5.25v13.5A2.25 2.25 0 003.75 21z"
        />
      </svg>
      <span className="text-sm">No image</span>
    </div>
  </div>
)

function FeatureSection({
  className,
  heading,
  headingColor,
  descriptionColor,
  bgColor = 'transparent',
  dataSource = 'built-in',
  features: customFeatures,
  enableScrollspy = true,
}: FeatureSectionProps) {
  const [activeTab, setActiveTab] = React.useState(0)
  const sectionRefs = React.useRef<(HTMLDivElement | null)[]>([])
  const router = useRouter()
  const { siteName } = useBranding()

  // Determine which features to display based on dataSource
  const rawFeatures = dataSource === 'custom' && customFeatures?.length ? customFeatures : getDefaultFeatures(siteName)
  const features = normalizeFeatures(rawFeatures)
  const displayHeading = heading ?? `Tham gia cộng đồng <span class="text-primary">Data & AI</span> cùng ${siteName}`

  const scrollToSection = (index: number) => {
    const element = sectionRefs.current[index]
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
    }
  }

  React.useEffect(() => {
    if (!enableScrollspy) return

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const index = sectionRefs.current.findIndex(ref => ref === entry.target)
            if (index !== -1) {
              setActiveTab(index)
            }
          }
        })
      },
      {
        threshold: 0.3,
        rootMargin: '-100px 0px -100px 0px',
      }
    )

    sectionRefs.current.forEach(ref => {
      if (ref) observer.observe(ref)
    })

    return () => observer.disconnect()
  }, [enableScrollspy])

  return (
    <Shell
      className={cn('px-3 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-6 md:py-8 lg:py-12 xl:py-16', className)}
      style={{ backgroundColor: bgColor !== 'transparent' ? bgColor : undefined }}
    >
      <PageHeaderHeading
        as="h2"
        size="lg"
        className="tracking-normal text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl/tight md:leading-tight text-center mb-6 sm:mb-8 md:mb-10 lg:mb-12"
        style={headingColor ? { color: headingColor } : undefined}
        dangerouslySetInnerHTML={{ __html: sanitizeHtmlValue(displayHeading) }}
      />

      <div>
        <div className="flex flex-col lg:flex-row gap-4 sm:gap-6 lg:gap-12">
          {enableScrollspy && (
            <div className="hidden lg:block w-64 flex-shrink-0">
              <div className="sticky top-24">
                <div className="bg-white/90 rounded-2xl px-6 py-4">
                  <div className="flex flex-col space-y-2">
                    {features.map((section, index) => (
                      <button
                        key={index}
                        onClick={() => scrollToSection(index)}
                        className={cn(
                          'px-4 py-3 rounded-xl text-sm font-medium transition-all duration-300 text-left',
                          activeTab === index
                            ? 'bg-primary text-white shadow-md'
                            : 'text-gray-600 hover:text-primary hover:bg-gray-100'
                        )}
                      >
                        {section.title || 'Untitled'}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {enableScrollspy && (
            <div className="lg:hidden sticky top-20 sm:top-20 z-30 mb-6 -mx-2 sm:mx-0">
              <div className="flex justify-center w-full">
                <div className="bg-white/95 backdrop-blur-sm border border-gray-200 rounded-xl px-2 py-2 shadow-lg w-full">
                  <div className="grid grid-cols-3 gap-1">
                    {features.map((section, index) => (
                      <button
                        key={index}
                        onClick={() => scrollToSection(index)}
                        className={cn(
                          'flex items-center justify-center px-1 py-2 text-[11px] sm:text-xs font-medium transition-all duration-300 rounded-lg whitespace-nowrap',
                          activeTab === index
                            ? 'bg-primary text-white shadow-md'
                            : 'text-gray-600 hover:text-primary hover:bg-gray-100'
                        )}
                      >
                        {section.mobileTitle || section.title || 'Untitled'}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="flex-1 space-y-6 sm:space-y-8 lg:space-y-12">
            {features.map((feature, index) => (
              <div
                key={index}
                ref={el => {
                  sectionRefs.current[index] = el
                }}
                className="space-y-4 sm:space-y-6 lg:space-y-8 scroll-mt-28 sm:scroll-mt-24"
                onClick={() => (feature.href && feature.href !== '#' ? router.push(feature.href) : undefined)}
              >
                <div className="relative rounded-2xl overflow-hidden transition-all duration-300 group cursor-pointer hover:bg-gray-50/50 p-2 sm:p-4 border border-gray-200 hover:border-primary hover:shadow-lg lg:p-6">
                  <div className="flex flex-col gap-6 lg:gap-12 items-start">
                    <div className="flex-1 space-y-4 lg:space-y-6 w-full">
                      <div className="flex items-start justify-between gap-4 w-full">
                        <h3 className="text-xl lg:text-3xl font-bold text-gray-900 leading-tight group-hover:text-primary transition-colors">
                          {feature.title || 'Untitled Feature'}
                        </h3>
                        <ArrowUpRight className="w-5 h-5 lg:w-6 lg:h-6 text-gray-400 group-hover:text-primary group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-300" />
                      </div>

                      {feature.description.length > 0 && (
                        <div className="space-y-3 lg:space-y-4">
                          {feature.description.map((desc, descIndex) => (
                            <div key={descIndex} className="flex items-start space-x-3 lg:space-x-4">
                              <div className="flex-shrink-0 w-2 h-2 bg-primary rounded-full mt-2 lg:mt-3" />
                              <p
                                className="text-sm lg:text-lg text-gray-700 leading-relaxed"
                                style={descriptionColor ? { color: descriptionColor } : undefined}
                                dangerouslySetInnerHTML={{ __html: sanitizeHtmlValue(desc) }}
                              />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="relative w-full self-center">
                      {feature.image ? (
                        <NextImage
                          src={feature.image}
                          alt={feature.imageAlt || feature.title || 'Feature image'}
                          width={1006}
                          height={606}
                          quality={100}
                          className="w-full h-auto"
                          sizes="(min-width: 1024px) 45vw, 100vw"
                          classNames={{
                            image: 'object-cover w-full h-auto rounded-2xl',
                          }}
                        />
                      ) : (
                        <ImagePlaceholder />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Shell>
  )
}

export default FeatureSection
